// ** React Imports
import React, { SyntheticEvent, useEffect, useState } from 'react';

// ** MUI Imports
import { Constant } from '../../../models/constant';
import 'react-datepicker/dist/react-datepicker.css'
import { Divider, styled, tableCellClasses, CardActions, Alert, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, useTheme, useMediaQuery, Typography, Button, Box, Card, Modal, Paper, Grid, FormControl, MenuItem, InputLabel, Select, CardContent } from '@mui/material';
import { useSelection } from '../../../components/context/SelectionContext';

// ** Third Party Styles Imports


import { Link, NavLink, useNavigate } from 'react-router-dom';
import Title from '../../../components/Title';
import axios from "axios";
import CancelIcon from '@mui/icons-material/Cancel';
import { Office } from '../../../models/office';
import { Checkbox } from '@mui/material';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import { useDrawer } from '../../../components/context/DrawerContext';
import { stickyListRegionWidth } from '../../../components/layouts/StickyListToolbar';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import PermissionButton from '../../../components/common/PermissionButton';

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

const AssignAdd: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {

    document.title = "Crear Constante";
    const navigate = useNavigate();

    const [selectedConstants, setSelectedConstants] = useState<number[]>([]);
    const [error, setError] = useState('');
    const [showError, setShowError] = useState(false);
    const [constants, setConstants] = useState<Constant[]>([]);
    const [offices, setOffices] = useState([]);
    const [unselectedConstants, setUnselectedConstants] = useState<Constant[]>([]);
    const { office_id, setOffice_id, collect_id, setCollect_id, user_id, setUser_id } = useSelection();
    const { drawerOpen } = useDrawer();
    const { canAssign } = useResourcePermissions('/administrar/constantes');

    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        [`&.${tableCellClasses.head}`]: {
            backgroundColor: '#9d9d9d',
            color: theme.palette.common.white,
        },
        [`&.${tableCellClasses.body}`]: {
            fontSize: 14,
        },
    }));

    const StyledTableRow = styled(TableRow)(({ theme }) => ({
        '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.action.hover,
        },
        '&:last-child td, &:last-child th': {
            border: 0,
        },
    }));

    useEffect(() => {
        (
            async () => {
                const { data } = await axios.get('constants');
                setConstants(data.data);
            }
        )();
    }, []);

    useEffect(() => {
        (
            async () => {
                const { data } = await axios.get('alloffices');
                setOffices(data.data);
            }
        )();
    }, []);
    useEffect(() => {
        if (office_id) {
            (
                async () => {
                    try {
                        const { data } = await axios.get(`constantoffices/${office_id}`);
                        // Verificar si data es un array antes de usar map
                        if (Array.isArray(data)) {
                            setSelectedConstants(data.map((item) => item.constant_id));
                        } else {
                            console.error("Data is not an array:", data);
                        }
                    } catch (error) {
                        console.error("Error fetching data:", error);
                    }
                }
            )();
        }
    }, [office_id]);

    const handleCheckboxChange = (constantId: number) => {
        setSelectedConstants((prevSelected) => {
            // Obtener la constante actual
            const currentConstant = constants.find((constant) => constant.id === constantId);

            if (currentConstant) {
                if (prevSelected.includes(constantId)) {
                    // Si ya está seleccionado, agregar a las no seleccionadas
                    setUnselectedConstants((prevUnselected) => [...prevUnselected, currentConstant]);
                    return prevSelected.filter((id) => id !== constantId);
                } else {
                    // Si no está seleccionado, quitar de las no seleccionadas
                    setUnselectedConstants((prevUnselected) =>
                        prevUnselected.filter((constant) => constant.id !== constantId)
                    );
                    return [...prevSelected, constantId];
                }
            }
            return prevSelected;
        });
    };
    const submit = async (event: SyntheticEvent) => {
        if (!canAssign) return;

        event.preventDefault();
        const unselectedConstantIds = unselectedConstants.map((constant) => constant.id);

        const formData = {
            constant_id: selectedConstants, // Utilizar el arreglo de elementos seleccionados
            office_id: [office_id], // Convertir office_id a un arreglo
            unselectedConstants: unselectedConstantIds, // Agregar constantes no seleccionadas
        };
        try {
            const response = await axios.post('/constantoffices', formData);
            navigate('/administrar/constantes');
            setError('');
            setShowError(false);
        } catch (error) {
            console.error('Error:', error);
        }
        // Manejar el error
    }


    return (
        <HeaderResponsive >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>

                <Title>Asignar Constantes a Oficinas</Title>
            </Box>
            <Card>
                <CardContent
                    sx={{
                        width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto',
                    }}
                >
                    <Box onSubmit={e => e.preventDefault()} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <InputLabel id='form-layouts-separator-select-label'>Seleccionar Oficinas</InputLabel>
                                {offices && offices.length > 0 ? (
                                    <Select
                                        label='Oficinas'
                                        value={office_id}
                                        id='form-layouts-separator-select'
                                        labelId='form-layouts-separator-select-label'
                                        onChange={(event) => setOffice_id(event.target.value)}
                                        required
                                    >
                                        {offices.map((office: Office) => {
                                            return (
                                                <MenuItem key={office.id} value={office.id}>
                                                    {office.name}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                ) : null}
                            </FormControl>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Title></Title>
                            </Box>
                            <TableContainer component={Paper}>
                                <Table sx={{ minWidth: 700 }} aria-label="customized table">
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell align="center">Id</StyledTableCell>
                                            <StyledTableCell align="center">Nombre</StyledTableCell>
                                            <StyledTableCell align="center">Tipo Constante</StyledTableCell>
                                            <StyledTableCell align="center">Seleccionar</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {constants && constants.length > 0 ? (
                                            constants.map((constant: Constant) => {
                                                return (
                                                    <StyledTableRow key={constant.id}>
                                                        <StyledTableCell align='center' component='th' scope='row'>
                                                            {constant.id}
                                                        </StyledTableCell>
                                                        <StyledTableCell align='center'>{constant.name}</StyledTableCell>
                                                        <StyledTableCell align='center'>{constant.type_constant.name}</StyledTableCell>
                                                        <StyledTableCell align='center'>
                                                            <Checkbox
                                                                checked={selectedConstants.includes(constant.id)}
                                                                onChange={() => handleCheckboxChange(constant.id)}
                                                            />
                                                        </StyledTableCell>
                                                    </StyledTableRow>
                                                );
                                            })
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={3}>No hay Información disponibles.</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell align="center">Id</StyledTableCell>
                                            <StyledTableCell align="center">Nombre</StyledTableCell>
                                            <StyledTableCell align="center">Tipo Constante</StyledTableCell>
                                            <StyledTableCell align="center">Seleccionar</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Box>
                </CardContent>
                <Divider sx={{ margin: 0 }} />
                <CardActions style={{ marginRight: 'auto' }}>
                    <PermissionButton
                        allowed={canAssign}
                        deniedTooltip="Sin permiso para asignar"
                        tooltip="Guardar asignación"
                        size='small'
                        type='submit'
                        sx={{ mr: 2 }}
                        variant='contained'
                        onClick={submit}
                    >
                        Guardar
                    </PermissionButton>
                    <Link to='/administrar/constantes'>
                        <Button size='small' color='secondary' variant='outlined'>
                            Cancelar
                        </Button>
                    </Link>
                </CardActions>
            </Card>

        </HeaderResponsive >

    )
};
export default AssignAdd
